import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Container,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@mui/material';
import {
    CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
    ShoppingCart as ShoppingCartIcon,
    Lock as LockIcon,
    Storage as StorageIcon,
    Cast,
    CheckBoxOutlined,
    WorkspacePremium
} from '@mui/icons-material';
import { NewPageTitleBar } from '@/components';
import AppCard from '@/components/appcard/AppCard';
import { useStore } from '@core/store';
import { ActiveLicenses } from '@/core/types';
import { getMenuData } from '@/core/data/menuData';
import { FCSecurePage } from '@/components/shared/navigation/securePage.ts';

interface LicenseItem {
    key: string;
    icon: React.ReactNode;
    label: string;
    value?: ActiveLicenses;
}

interface InfoData {
    version: string;
    authTenant: string;
    authPolicy: string;
    authClientId: string;
    aiKey: string;
    apiUrl?: string;
}

const Info: FCSecurePage = () => {
    const { t } = useTranslation();
    const hasLicense = useStore((state) => state.hasLicense);
    const apiInfo = useStore((state) => state.apiInfo);
    const loadApiInfo = useStore((state) => state.loadApiInfo);

    useEffect(() => {
        if (!apiInfo?.version) {
            loadApiInfo();
        }
    }, [apiInfo, loadApiInfo]);

    const uiInfo: InfoData = useMemo(
        () => ({
            version: import.meta.env.VITE_VERSION || '',
            authTenant: import.meta.env.VITE_AUTH_TENANT || '',
            authPolicy: import.meta.env.VITE_AUTH_POLICY || '',
            authClientId: import.meta.env.VITE_AUTH_CLIENTID || '',
            aiKey: import.meta.env.VITE_INSTRUMENTATIONKEY || '',
            apiUrl: import.meta.env.VITE_API || ''
        }),
        []
    );

    // Create a default InfoData object for API info
    const defaultApiInfo: InfoData = useMemo(
        () => ({
            version: '',
            authTenant: '',
            authPolicy: '',
            authClientId: '',
            aiKey: '',
            apiUrl: ''
        }),
        []
    );

    const licenses: LicenseItem[] = useMemo(
        () => [
            {
                key: 'basic',
                icon: <WorkspacePremium />,
                label: 'baseLicense',
                value: ActiveLicenses.Basic
            },
            {
                key: 'rfid',
                icon: <Cast sx={{ transform: 'rotate(180deg)' }} />,
                label: 'rfid',
                value: ActiveLicenses.Rfid
            },
            {
                key: 'shopAutomation',
                icon: <ShoppingCartIcon />,
                label: 'shopAutomation',
                value: ActiveLicenses.ShopAutomation
            },
            {
                key: 'hybridLoc',
                icon: <LockIcon />,
                label: 'hybridLoc',
                value: ActiveLicenses.HybridLoc
            },
            {
                key: 'smartShelf',
                icon: <StorageIcon />,
                label: 'smartShelf',
                value: ActiveLicenses.SmartShelf
            },
            {
                key: 'fastLaner',
                icon: <ShoppingCartIcon />,
                label: 'fastLaner',
                value: ActiveLicenses.FastLaner
            }
        ],
        []
    );

    const maskString = (str: string, unmaskedLength: number, maskFromStart: boolean = true): string => {
        if (!str || !unmaskedLength) return '';
        const maskStart = maskFromStart ? 0 : Math.max(0, unmaskedLength);
        const maskEnd = maskFromStart ? Math.max(0, str.length - unmaskedLength) : str.length;
        return str
            .split('')
            .map((char, index) => {
                if (index >= maskStart && index < maskEnd && char !== '-') {
                    return 'X';
                }
                return char;
            })
            .join('');
    };

    const renderInfoSection = (title: string, icon: string, data: InfoData) => (
        <Box
            sx={{
                width: '100%',
                padding: '12px',
                height: '480px',
                '@media (min-width: 960px)': {
                    flex: '0 0 50%',
                    maxWidth: '50%'
                }
            }}
        >
            <AppCard heading={title} icon={icon} customClasses="mb-0">
                <Box component="dl" sx={{ m: 0, mt: -3 }}>
                    <Box component="dt" sx={{ fontWeight: 'bold', mt: 2 }}>
                        {t('version')}
                    </Box>
                    <Box component="dd" sx={{ ml: 0, mb: 2 }}>
                        {data.version}
                    </Box>

                    <Box component="dt" sx={{ fontWeight: 'bold', mt: 2 }}>
                        Auth-Tenant
                    </Box>
                    <Box component="dd" sx={{ ml: 0, mb: 2 }}>
                        {data.authTenant}
                    </Box>

                    <Box component="dt" sx={{ fontWeight: 'bold', mt: 2 }}>
                        Auth-Policy
                    </Box>
                    <Box component="dd" sx={{ ml: 0, mb: 2 }}>
                        {data.authPolicy}
                    </Box>

                    <Box component="dt" sx={{ fontWeight: 'bold', mt: 2 }}>
                        Auth-ClientId
                    </Box>
                    <Box component="dd" sx={{ ml: 0, mb: 2 }}>
                        {maskString(data.authClientId, 12)}
                    </Box>

                    <Box component="dt" sx={{ fontWeight: 'bold', mt: 2 }}>
                        AI-Key
                    </Box>
                    <Box component="dd" sx={{ ml: 0, mb: 2 }}>
                        {maskString(data.aiKey, 12)}
                    </Box>

                    <Box component="dt" sx={{ fontWeight: 'bold', mt: 2, minHeight: '1.5em' }}>
                        {data.apiUrl ? 'API-Url' : ''}
                    </Box>
                    <Box component="dd" sx={{ ml: 0, mb: 1, minHeight: '1.5em' }}>
                        {data.apiUrl || ''}
                    </Box>
                </Box>
            </AppCard>
        </Box>
    );

    return (
        <Box className="no-scrollbar">
            <NewPageTitleBar
                icon={<i className="zmdi zmdi-settings" />}
                title={t('info')}
                menus={getMenuData()}
                iconSize="20px"
                currentRoute={{ path: '/manage-organisation/info' }}
                t={t}
            />

            <Container maxWidth={false} sx={{ pt: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', margin: '-12px' }}>
                    <Box
                        sx={{
                            width: '100%',
                            flex: '0 0 100%',
                            padding: '12px'
                        }}
                    >
                        <AppCard
                            icon="workspace_premium"
                            iconClass="material-icons-outlined"
                            heading={t('licenses')}
                            customClasses="mb-0"
                            fullBlock={true}
                        >
                            <TableContainer sx={{ mt: 2, overflow: 'hidden' }}>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{ textAlign: 'left' }}>{t('name')}</TableCell>
                                            <TableCell sx={{ textAlign: 'center' }}>{t('acquired')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {licenses.map((license) => (
                                            <TableRow key={license.key}>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            gap: 1
                                                        }}
                                                    >
                                                        {license.icon}
                                                        <Typography component="span">{t(license.label)}</Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'center'
                                                        }}
                                                    >
                                                        {hasLicense(license.value || []) ? (
                                                            <CheckBoxOutlined />
                                                        ) : (
                                                            <CheckBoxOutlineBlankIcon />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </AppCard>
                    </Box>

                    {renderInfoSection('UI', 'visibility', uiInfo)}
                    {renderInfoSection('API', 'code', apiInfo || defaultApiInfo)}
                </Box>
            </Container>
        </Box>
    );
};

export default Info;
